import { useEffect } from 'react';

import { ChronicDiseaseSimple } from '@dreryk/egabinet-patient-medical-information-frontend-api';
import { Visit } from '@dreryk/egabinet-visit-frontend-api';

import { FormValidationSnackbar, Scroll } from '@dreryk/react-components';

import { createPatientWithExternalIdentifierProtector } from '~libs.shared.visit';

import { useVisitFormControllerRegistration } from '../../common/visitForm/VisitFormContext';

import { ConfirmVisitAction } from './actionBar/confirmVisitAction/ConfirmVisitAction';
import { ConfirmVisitLaterAction } from './actionBar/confirmVisitLaterAction/ConfirmVisitLaterAction';
import { ConfirmVisitSummaryAction } from './actionBar/confirmVisitSummaryAction/ConfirmVisitSummaryAction';
import { ExaminationTabs } from './examinations/ExaminationTabs';
import { StyledVisitForm } from './StyledVisitForm';
import { StyledVisitFormContent } from './StyledVisitFormContent';
import { StyledVisitFormRecommendationsAndContinousMedicineContainer } from './StyledVisitFormRecommendationsAndContinousMedicineContainer';
import { VisitFormTabs } from './tabs/VisitFormTabs';
import { VisitContinuouslyUsedMedicineTable } from './VisitContinuouslyUsedMedicineTable/VisitContinuouslyUsedMedicineTable';
import { useVisitContinuouslyUsedMedicineQuery } from './VisitContinuouslyUsedMedicineTable/VisitContinuouslyUsedMedicineTable.hooks';
import { useVisitFormController } from './visitForm.hooks';
import { VisitFormAutoSave } from './VisitFormAutoSave';
import { VisitFormRouteLeavingGuard } from './VisitFormRouteLeavingGuard';
import { VisitFormSynchronization } from './VisitFormSynchronization';
import { VisitTemplatesControlsBarActionsPortal } from './VisitTemplateControlsBarActionsPortal/VisitTemplatesControlsBarActionsPortal';
import { InterviewTextEditor } from './interview/InterviewTextEditor';
import { PreInterviewPreviewLink } from './preInterview/PreInterviewPreviewLink';
import { RecommendationsTextEditor } from './recommendations/RecommendationsTextEditor';

type VisitFormProps = {
   readonly visit: Visit;
   readonly chronicDiseases: ChronicDiseaseSimple[];
};

// eslint-disable-next-line max-lines-per-function
export const VisitForm = (props: VisitFormProps): JSX.Element | null => {
   const controller = useVisitFormController(props.visit);
   const { data: continuouslyUsedMedicines } = useVisitContinuouslyUsedMedicineQuery(props.visit.id);
   const protectByPatientWithExternalIdentifier = createPatientWithExternalIdentifierProtector(props.visit.patient);
   const registerController = useVisitFormControllerRegistration();

   useEffect(() => {
      registerController(controller);
   }, [registerController, controller]);

   const showContinuouslyUsedMedicines = Boolean(
      protectByPatientWithExternalIdentifier('issuingPrescriptions', Boolean(continuouslyUsedMedicines?.length))
   );

   return (
      <>
         <Scroll testId={'visit'}>
            <StyledVisitForm noValidate>
               <StyledVisitFormContent continuouslyUsedMedicines={showContinuouslyUsedMedicines} testId={'visit'}>
                  <InterviewTextEditor
                     practitionerId={props.visit.practitionerId}
                     visitId={props.visit.id}
                     controller={controller.register('interview')}
                     labelSuffix={<PreInterviewPreviewLink appointmentId={props.visit.appointmentId} />}
                  />
                  <VisitFormTabs
                     controller={controller}
                     practitionerId={props.visit.practitionerId}
                     visit={props.visit}
                     chronicDiseases={props.chronicDiseases}
                  />
                  <ExaminationTabs
                     controller={controller}
                     visitId={props.visit.id}
                     patientId={props.visit.patient.id}
                     practitionerId={props.visit.practitionerId}
                  />
                  <StyledVisitFormRecommendationsAndContinousMedicineContainer>
                     <RecommendationsTextEditor
                        controller={controller.register('recommendations')}
                        patientId={props.visit.patient.id}
                        practitionerId={props.visit.practitionerId}
                        visitId={props.visit.id}
                     />
                     {showContinuouslyUsedMedicines ? (
                        <VisitContinuouslyUsedMedicineTable
                           visitId={props.visit.id}
                           patientId={props.visit.patient.id}
                           continuouslyUsedMedicines={continuouslyUsedMedicines}
                           visitDiagnoses={props.visit.diagnoses}
                        />
                     ) : null}
                  </StyledVisitFormRecommendationsAndContinousMedicineContainer>
               </StyledVisitFormContent>
            </StyledVisitForm>
         </Scroll>
         <FormValidationSnackbar formState={controller.formState} />
         <VisitFormRouteLeavingGuard
            visitId={props.visit.id}
            appointmentId={props.visit.appointmentId}
            controller={controller}
         />
         <VisitFormAutoSave visitId={props.visit.id} controller={controller} />
         <VisitFormSynchronization
            visitId={props.visit.id}
            chronicDiseases={props.chronicDiseases}
            controller={controller}
         />
         <ConfirmVisitLaterAction
            handleSubmitDraft={controller.handleSubmitDraft}
            control={controller.control}
            appointmentId={props.visit.appointmentId}
            visitId={props.visit.id}
            trigger={controller.trigger}
         />
         <ConfirmVisitAction
            handleSubmit={controller.handleSubmit}
            appointmentId={props.visit.appointmentId}
            visitId={props.visit.id}
            trigger={controller.trigger}
            control={controller.control}
         />
         <ConfirmVisitSummaryAction
            handleSubmit={controller.handleSubmit}
            appointmentId={props.visit.appointmentId}
            visitId={props.visit.id}
            patientId={props.visit.patient.id}
            trigger={controller.trigger}
            control={controller.control}
         />
         <VisitTemplatesControlsBarActionsPortal visit={props.visit} formController={controller} />
      </>
   );
};
